import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import toast from '@lib/components/Toast/Toast';
import { GetUrlParams } from '@lib/enums/urls';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import getAllTableItemsIds from '@lib/utils/getAllTableItemsIds';
import { Value } from '@lib/interfaces/form';
import {
  Documentation,
  DocumentationScopeNameEnum,
  DocumentationValidationStatusEnum,
  useAssetsSearchListLazyQuery,
  useEdmDocumentationBulkDestroyMutation,
  useEdmDocumentationBulkUpdateValidationStatusMutation,
  useEdmDocumentationCategoriesSearchListLazyQuery,
  useSectorsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useSitesSearchListLazyQuery,
} from 'graphql-common';
import { useEdmDocumentationsLightQuery } from '@lib/graphql-common';
import getValidationStatusSuccessKey from '@lib/utils/documentation/getValidationStatusSuccessKey';
import useEdmDocumentationsQueryHook from 'utils/fetch/hooks/useEdmDocumentationsQueryHook';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import { APP_URLS } from 'constants/urls';
import { getDocumentationCategoriesLoadOptions } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getSectorsLoadOptions } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions } from 'utils/fetch/searchLists/sitesSearchList';
import { getSiteAreasLoadOptions } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getAssetsLoadOptions } from 'utils/fetch/searchLists/assetsSearchList';
import useAiBudgetStatus from 'components/AiAssistant/hooks/useAiBudgetStatus';
import getDocumentsTableColumns from './getDocumentsTableColumns';
import getTableActionsMenu from './getTableActionsMenu';
import getTableActionsForSelected from './getTableActionsForSelected';
import useDocumentFilterFields, {
  DocumentFilterFieldNames,
} from './useDocumentFilterFields';

interface UseDocumentListActionsProps {
  filterParams: { [key: string]: Value };
  isAiDetectionEnabled?: boolean;
  isApprovals?: boolean;
  isFavoritedMode: boolean;
  isViewLocked?: boolean;
  isViewOnly?: boolean;
  setIsFavoritedMode: (
    updater: (prev: boolean | null | undefined) => boolean | null | undefined,
  ) => void;
  useBulkSelectResult: UseBulkSelectResult<Documentation>;
}

export default function useDocumentListActions({
  filterParams,
  isAiDetectionEnabled,
  isApprovals,
  isFavoritedMode,
  isViewLocked,
  isViewOnly,
  setIsFavoritedMode,
  useBulkSelectResult,
}: UseDocumentListActionsProps) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const [searchQuery] = useSearchParam();
  const [paginationParams, setPaginationParams] = usePaginationParams();

  // Search list lazy queries
  const [fetchCategories] = useEdmDocumentationCategoriesSearchListLazyQuery();
  const [fetchSectors] = useSectorsSearchListLazyQuery();
  const [fetchSites] = useSitesSearchListLazyQuery();
  const [fetchAreas] = useSiteAreasSearchListLazyQuery();
  const [fetchAssets] = useAssetsSearchListLazyQuery();

  // Filter fields
  const filterFields = useDocumentFilterFields({
    getCategoriesLoadOptions:
      getDocumentationCategoriesLoadOptions(fetchCategories),
    getSectorsLoadOptions: getSectorsLoadOptions(fetchSectors),
    getSitesLoadOptions: getSitesLoadOptions(fetchSites, undefined, {
      includeSector: true,
    }),
    getAreasLoadOptions: getSiteAreasLoadOptions(fetchAreas),
    getEquipmentLoadOptions: getAssetsLoadOptions(fetchAssets),
  });

  // Filter counter
  const filterCounter = useMemo(() => {
    let count = 0;
    Object.values(DocumentFilterFieldNames).forEach((fieldName) => {
      if (_get(filterParams, [fieldName, 'length'])) {
        count += 1;
      }
    });
    return count;
  }, [filterParams]);

  const { currentUserId } = useWhoamiQueryHook({ includeFullData: false });

  const documentationsQueryHookResult = useEdmDocumentationsQueryHook({
    approverId: isApprovals ? currentUserId : undefined,
    filterParams,
    isAiDetectionEnabled,
    isViewLocked,
    paginationParams,
    searchQuery,
    // BE gates `Favorited` scope behind documentation_management upgrade. The
    // chip UI already disables the toggle for locked users, but `favorites=true`
    // can survive a URL/deep-link/back-nav into a locked state — force-drop the
    // scope here so we never send an UPGRADE_PLAN_REQUIRED-bound query.
    scope:
      isFavoritedMode && !isViewLocked
        ? { name: DocumentationScopeNameEnum.Favorited }
        : undefined,
    skip: (isApprovals && !currentUserId) || undefined,
    skipCategory: isViewLocked,
  });

  const favoritedCountQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: 1,
      scope: { name: DocumentationScopeNameEnum.Favorited },
    },
    skip: isViewLocked,
  });

  const favoritedCount =
    favoritedCountQuery.data?.data?.metadata?.totalCount ?? 0;

  const { collection, firstLoading, loading, totalCount, totalPages } =
    documentationsQueryHookResult;

  const { credits } = useAiBudgetStatus(!!isAiDetectionEnabled);

  // Actions

  const onFavoriteCompleted = useCallback(
    (wasFavorited: boolean) => {
      favoritedCountQuery.refetch();
      if (!isFavoritedMode) {
        // Cache is already updated via cache.modify in DocumentFavoriteCell,
        // no need to refetch the main query
        return;
      }
      if (wasFavorited) {
        // Item was removed from favorites while in favorites mode —
        // handle pagination like a destroy
        onDestroyListItemCallBack({
          collection,
          navigate,
          pathname,
          refetch: documentationsQueryHookResult.refetch,
          search,
          totalPages,
        });
      } else {
        documentationsQueryHookResult.refetch();
      }
    },
    [
      collection,
      documentationsQueryHookResult,
      favoritedCountQuery,
      isFavoritedMode,
      navigate,
      pathname,
      search,
      totalPages,
    ],
  );

  const [bulkUpdateValidationStatus] =
    useEdmDocumentationBulkUpdateValidationStatusMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onValidationStatusChange = useCallback(
    (id: string, newStatus: DocumentationValidationStatusEnum) => {
      bulkUpdateValidationStatus({
        variables: {
          ids: [id],
          attributes: { validationStatus: newStatus },
        },
        onCompleted: () => {
          toast({ content: t(getValidationStatusSuccessKey(newStatus)) });
          documentationsQueryHookResult.refetch();
        },
      });
    },
    [bulkUpdateValidationStatus, documentationsQueryHookResult, t],
  );

  const onStatusBulkChangeHandler = useCallback(
    (newStatus: DocumentationValidationStatusEnum) => {
      tableItemsBulkAction<Documentation>({
        action: (ids) => {
          bulkUpdateValidationStatus({
            variables: {
              ids,
              attributes: { validationStatus: newStatus },
            },
            onCompleted: () => {
              toast({ content: t(getValidationStatusSuccessKey(newStatus)) });
              useBulkSelectResult.handleUnselectAllTableItems();
              documentationsQueryHookResult.refetch();
            },
          });
        },
        onGetAllIds: () =>
          getAllTableItemsIds<Documentation>({
            fetchIds: documentationsQueryHookResult.fetchIds,
            useBulkSelectResult,
          }),
        useBulkSelectResult,
      });
    },
    [
      bulkUpdateValidationStatus,
      documentationsQueryHookResult,
      t,
      useBulkSelectResult,
    ],
  );

  const onOpen = useCallback(
    (item: Documentation) => {
      navigate(
        APP_URLS.dashboard.documentation.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  const onToggleFavoritedMode = useCallback(() => {
    setIsFavoritedMode((prev) => !prev);
    setPaginationParams({ [GetUrlParams.Page]: '1' });
  }, [setIsFavoritedMode, setPaginationParams]);

  // Delete
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const isDeleteModalOpen = deleteIds.length > 0;

  const [destroyMutation] = useEdmDocumentationBulkDestroyMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });

  const onDelete = useCallback((item: Documentation) => {
    setDeleteIds([item.id]);
  }, []);

  const onBulkDelete = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setDeleteIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeDeleteModal = useCallback(() => {
    setDeleteIds([]);
  }, []);

  const onConfirmedDelete = useCallback(() => {
    if (!deleteIds.length) return;
    const idsToDelete = [...deleteIds];
    destroyMutation({
      variables: { ids: idsToDelete },
      onCompleted: () => {
        setDeleteIds([]);
        useBulkSelectResult.handleUnselectAllTableItems();
        toast({ content: t('destroy-documents-success') });
        onDestroyListItemCallBack({
          collection,
          collectionForDestroy: idsToDelete,
          navigate,
          pathname,
          refetch: documentationsQueryHookResult.refetch,
          search,
          totalPages,
        });
        favoritedCountQuery.refetch();
      },
    });
  }, [
    collection,
    deleteIds,
    destroyMutation,
    documentationsQueryHookResult,
    favoritedCountQuery,
    navigate,
    pathname,
    search,
    t,
    totalPages,
    useBulkSelectResult,
  ]);

  // File viewer & version notes modals
  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  const closeFileViewer = useCallback(() => {
    setFileViewerDocument(null);
  }, []);

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const closeVersionNotes = useCallback(() => {
    setVersionNotesDocument(null);
  }, []);

  // Table configuration

  const tableColumns = useMemo(
    () =>
      getDocumentsTableColumns({
        onFavoriteCompleted,
        onOpen,
        onValidationStatusChange,
        isApprovals,
        isViewLocked,
        isAiDetectionEnabled,
        credits,
        t,
      }),
    [
      onFavoriteCompleted,
      onOpen,
      onValidationStatusChange,
      isApprovals,
      isViewLocked,
      isAiDetectionEnabled,
      credits,
      t,
    ],
  );

  const tableActionsMenu = useMemo(
    () =>
      getTableActionsMenu({
        onViewDocument,
        onVersionNotes,
        onDetails: (item) =>
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id: item.id },
            }),
          ),
        onEdit: isViewOnly
          ? undefined
          : (item) =>
              navigate(
                APP_URLS.dashboard.documentation.edit.getDynamicPath({
                  pathParts: { id: item.id },
                }),
              ),
        onDelete: isViewOnly ? undefined : onDelete,
        t,
      }),
    [isViewOnly, navigate, onDelete, onVersionNotes, onViewDocument, t],
  );

  // Bulk edit
  const [bulkEditIds, setBulkEditIds] = useState<string[]>([]);
  const isBulkEditModalOpen = bulkEditIds.length > 0;

  const onBulkEdit = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setBulkEditIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeBulkEditModal = useCallback(() => {
    setBulkEditIds([]);
  }, []);

  const tableActionsForSelected = useMemo(
    () =>
      getTableActionsForSelected({
        isApprovals,
        isViewLocked,
        isViewOnly,
        onBulkEdit,
        onDelete: onBulkDelete,
        onStatusBulkChangeHandler,
        selectedItemsCount:
          useBulkSelectResult?.getSelectedItemsCount(totalCount),
        t,
      }),
    [
      isApprovals,
      isViewLocked,
      isViewOnly,
      onBulkDelete,
      onBulkEdit,
      onStatusBulkChangeHandler,
      totalCount,
      useBulkSelectResult,
      t,
    ],
  );

  const isEmptyState = !loading && collection.length === 0 && !searchQuery;

  return {
    bulkEditIds,
    closeBulkEditModal,
    closeDeleteModal,
    closeFileViewer,
    closeVersionNotes,
    collection,
    deleteIds,
    error: documentationsQueryHookResult.error,
    favoritedCount,
    fileViewerDocument,
    filterCounter,
    filterFields,
    firstLoading,
    isBulkEditModalOpen,
    isDeleteModalOpen,
    isEmptyState,
    loading,
    onConfirmedDelete,
    onToggleFavoritedMode,
    refetch: documentationsQueryHookResult.refetch,
    searchQuery,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    totalCount,
    useBulkSelectResult,
    versionNotesDocument,
  };
}
